import type { QueryClient } from "@tanstack/react-query";

import type { FilterDefinition } from "@/shared/components/ResourceView";
import { QueryContentMode, QueryTarget } from "@/shared/domain/query";
import type { Filter, FilterMatchMode } from "@/shared/domain/query/filters";
import {
  allQueryResultsOptions,
  queryResultCountOptions,
  structuredQueryOptions,
  type UseStructuredQueryOptions,
} from "@/shared/domain/query/hooks";
import type { QueryService } from "@/shared/domain/query/QueryService";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks";
import type { SessionRecord } from "@/shared/domain/sessions/SessionRecord";

import { sessionsQueryBody } from "./sessionFilters";

/**
 * Newest start first. Sessions with no start sort last (the query catalog
 * pins `min_timestamp_ns` to NULLS LAST).
 */
const NEWEST_FIRST = {
  columnId: "min_timestamp_ns",
  direction: "desc",
} as const;

/**
 * The query for a device's sessions under `filters`: the scope the Sessions
 * tab pins, so the timeline shows what the table shows. The device Overview
 * and its route loader both build it here, so a prefetch lands in the cache
 * entry the page reads.
 */
export function deviceSessionsQuery({
  orgId,
  deviceId,
  filters,
  definitions,
}: {
  orgId: string;
  deviceId: string;
  /** The user's filters on the sessions, and how they combine. */
  filters: { filters: Filter[]; matchMode: FilterMatchMode };
  /** Shape the conditions of the user's filters; with none, they change nothing. */
  definitions: FilterDefinition[];
}): UseStructuredQueryOptions {
  const query = sessionsQueryBody({
    scope: [
      {
        type: "string",
        field: "device.device_id",
        comparator: "EQUALS",
        values: [deviceId],
      },
    ],
    filters: { type: "structured", ...filters },
    definitions,
    sort: NEWEST_FIRST,
  });
  return {
    resolvedOrgId: orgId,
    query,
    target: QueryTarget.Sessions,
    contentMode: QueryContentMode.RecordOnly,
    queryKey: sessionQueryKeys.list(orgId, query),
    // The timeline's tracks and activity strip are built from one result set;
    // a refetch while the page is open would change one under the other. New
    // sessions show on a reload or a filter change.
    staleTime: Infinity,
  };
}

/**
 * Submit `submitOptions` and fetch its rows and count into the cache the
 * device Overview's hooks read. Never rejects: a failure is left for the page's own
 * queries to retry and show.
 */
export async function prefetchDeviceSessions(
  queryClient: QueryClient,
  queryService: QueryService,
  submitOptions: UseStructuredQueryOptions,
): Promise<void> {
  try {
    const { query_id } = await queryClient.ensureQueryData(
      structuredQueryOptions(queryService, submitOptions),
    );
    await Promise.all([
      queryClient.prefetchQuery(
        allQueryResultsOptions<SessionRecord>(queryService, query_id),
      ),
      queryClient.prefetchQuery(
        queryResultCountOptions(queryService, query_id),
      ),
    ]);
  } catch {
    // The page submits the same query and reports its failure.
  }
}
